import { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Settings, Moon, Globe, Bell, FileCheck2, Download, Loader2, Cookie, CreditCard } from 'lucide-react';
import { tuvDocsAPI, billingAPI } from '@/lib/api';
import { clearConsent, getConsent } from '@/lib/consent';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const isAdmin = user?.role === 'admin';

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await billingAPI.createPortalSession(`${window.location.origin}/settings`);
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('Stripe did not return a portal URL');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 400 && /No billing account/i.test(detail || '')) {
        toast.info('You do not have an active subscription yet — visit /pricing to subscribe first.');
      } else {
        toast.error(detail || 'Failed to open billing portal');
      }
    } finally {
      setOpeningPortal(false);
    }
  };

  const downloadTuv = async () => {
    setDownloading(true);
    try {
      const res = await fetch(tuvDocsAPI.downloadZipUrl(), { credentials: 'include' });
      if (res.status === 403) { toast.error('Admin access required'); return; }
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename=([^;]+)/i);
      a.download = m ? m[1].trim() : 'Veridex_TUV_Pack.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('TÜV package downloaded');
    } catch (e) {
      toast.error(e.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-6">
      <h1 className="cp-page-title flex items-center gap-2"><Settings className="w-6 h-6 text-muted-foreground" /> Settings</h1>

      <div className="max-w-2xl space-y-4">
        <Card className="p-5 animate-fade-in stagger-1" data-testid="setting-dark-mode">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Moon className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Dark Mode</p>
                <p className="text-xs text-muted-foreground">Toggle between light and dark theme</p>
              </div>
            </div>
            <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="settings-theme-switch" />
          </div>
        </Card>

        <Card className="p-5 animate-fade-in stagger-2" data-testid="setting-language">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Language</p>
                <p className="text-xs text-muted-foreground">English (US)</p>
              </div>
            </div>
            <span className="text-xs text-muted-foreground">Coming soon</span>
          </div>
        </Card>

        <Card className="p-5 animate-fade-in stagger-3" data-testid="setting-notifications">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Notifications</p>
                <p className="text-xs text-muted-foreground">Receive email alerts for CBAM updates</p>
              </div>
            </div>
            <Switch defaultChecked data-testid="settings-notifications-switch" />
          </div>
        </Card>

        {isAdmin && (
          <Card className="p-5 animate-fade-in stagger-4" data-testid="setting-tuv-package">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <FileCheck2 className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">TÜV Documentation Package</p>
                  <p className="text-xs text-muted-foreground">
                    5 Markdown documents (EN/DE methodology, audit guide, factor sources, checklist) bundled as a ZIP.
                  </p>
                </div>
              </div>
              <Button onClick={downloadTuv} disabled={downloading} data-testid="download-tuv-btn">
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Download TÜV Package
              </Button>
            </div>
          </Card>
        )}

        <Card className="p-5 animate-fade-in stagger-5" data-testid="setting-billing">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Billing & subscription</p>
                <p className="text-xs text-muted-foreground">
                  Manage your plan, update payment method, view invoices, or cancel —
                  powered by Stripe.
                </p>
              </div>
            </div>
            <Button onClick={openBillingPortal} disabled={openingPortal} data-testid="manage-billing-btn">
              {openingPortal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Manage Billing
            </Button>
          </div>
        </Card>

        <Card className="p-5 animate-fade-in stagger-5" data-testid="setting-cookie-consent">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Cookie className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Cookie preferences</p>
                <p className="text-xs text-muted-foreground" data-testid="consent-status">
                  Current choice: <b>{getConsent() || 'not decided'}</b>. You can change your mind at any time.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                clearConsent();
                toast.success('Choice cleared — the banner will reappear on next page load');
                setTimeout(() => window.location.reload(), 800);
              }}
              data-testid="reset-consent-btn"
            >
              Change preferences
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
